import React, { useState } from 'react';
import {
  GoogleAuthProvider, signInWithPopup,
} from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

interface Props { onBack: () => void; }

export const Login: React.FC<Props> = ({ onBack }) => {
  const [googleLoad,  setGoogleLoad] = useState(false);
  const [error,       setError]      = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const returnTo = location.state?.returnTo || '/';

  const friendlyError = (code: string) => {
    const map: Record<string, string> = {
      'auth/too-many-requests':   'Too many failed attempts. Please try again later.',
      'auth/popup-closed-by-user': 'Sign in was cancelled.',
    };
    return map[code] || 'Something went wrong during sign in. Please try again.';
  };

  const handleGoogle = async () => {
    setGoogleLoad(true);
    setError('');
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      navigate(returnTo);
    } catch (err: unknown) {
      setError(friendlyError((err as { code?: string }).code ?? ''));
      setGoogleLoad(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center px-4">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-purple-600/10 blur-[120px]" />
      </div>

      <div className="relative w-full max-w-md animate-fade-in">
        {/* Back button */}
        <button onClick={onBack} className="flex items-center gap-2 text-gray-500 hover:text-gray-300 text-sm mb-8 transition-colors">
          <ArrowLeft size={16} /> Back to home
        </button>

        {/* Card */}
        <div className="p-8 rounded-3xl bg-[#0D0D1A] border border-white/8 shadow-2xl">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-[#0D0D1A] flex items-center justify-center shadow-xl shadow-purple-500/30 mb-4 overflow-hidden">
              <img src="/nxtkonekt-logo.svg" alt="NxtKonekt" className="w-full h-full" />
            </div>
            <h1 className="text-2xl font-black text-white">
              Nxt<span className="text-purple-400">Konekt</span> ProQuote
            </h1>
            <p className="text-gray-500 text-sm mt-1">Sign in to continue</p>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm mb-4">
              <AlertCircle size={16} className="shrink-0" />
              {error}
            </div>
          )}

          {/* Google */}
          <button
            onClick={handleGoogle}
            disabled={googleLoad}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border border-white/10 hover:border-purple-500/40 hover:bg-white/5 text-white font-medium text-sm transition-all duration-200 disabled:opacity-50"
          >
            {googleLoad ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            )}
            Continue with Google
          </button>

          <p className="mt-6 text-center text-xs text-gray-600">
            By signing in, you agree to NxtKonekt's Partner Terms of Service
          </p>
        </div>
      </div>
    </div>
  );
};
